import { ComponentPropsWithRef, ForwardedRef, forwardRef } from 'react';
import { Button } from '@givebutter/primitives/button';
import { faChevronRight, faComment } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { show as showIntercomChat } from '@intercom/messenger-js-sdk';
import * as DropdownMenu from 'components/dropdown-menu';
import { NucleoIcon } from 'components/nucleo-icon';
import { IntercomProductTourTargets } from 'constants/intercom';
import {
    ACADEMY_LINK,
    BUTTER_BLOG_LINK,
    FREEBIES_LINK,
    GIVEBUTTER_CHANGELOG_LINK,
    HELP_CENTER_LINK,
    OFFICE_HOURS_LINK,
    ROADMAP_LINK,
    WEBINARS_LINK,
} from 'constants/links';
import { twMerge } from 'tailwind-merge';

const HelpMenu = () => {
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <Button
                    variant="transparent"
                    shape={'square'}
                    className="hover:!bg-gray-700 focus:!bg-gray-700"
                    data-intercom-target={IntercomProductTourTargets.HeaderHelp}
                    aria-label="Help"
                >
                    <NucleoIcon icon="circleQuestion" className="text-white" />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={2}>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={HELP_CENTER_LINK}>Help center</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={ACADEMY_LINK}>Givebutter Academy</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={OFFICE_HOURS_LINK}>Office Hours</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={WEBINARS_LINK}>Webinars</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={FREEBIES_LINK}>Freebies</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={BUTTER_BLOG_LINK}>Butter Blog</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={ROADMAP_LINK}>Product roadmap</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                        <HelpMenuLink href={GIVEBUTTER_CHANGELOG_LINK}>Changelog</HelpMenuLink>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                        className="flex items-center gap-2 bg-gray-100 !p-3 transition hover:!bg-gray-200"
                        onSelect={() => showIntercomChat()}
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex size-8 items-center justify-center rounded-lg border border-gray-200 bg-white">
                                <FontAwesomeIcon icon={faComment} className="text-blue-500" />
                            </div>
                            <div className="flex flex-col">
                                <p className="text-sm font-medium text-blue-600">Chat with us</p>
                                <p className="text-xs font-medium text-gray-700">
                                    24/7 <span className="font-normal">&mdash; even holidays!</span>
                                </p>
                            </div>
                            <FontAwesomeIcon icon={faChevronRight} className="text-blue-500" />
                        </div>
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

interface HelpMenuItemProps extends ComponentPropsWithRef<'a'> {}
const HelpMenuLink = forwardRef(
    (
        { className, children, ...rest }: HelpMenuItemProps,
        forwardedRef: ForwardedRef<HTMLAnchorElement>,
    ) => {
        return (
            <a
                target="_blank"
                rel="noreferrer"
                className={twMerge(
                    'flex items-center gap-2 text-gray-700 focus-visible:before:border-0',
                    className,
                )}
                ref={forwardedRef}
                {...rest}
            >
                {children}
                <NucleoIcon icon="shareUpRight" className="text-blue-500" />
            </a>
        );
    },
);

export { HelpMenu };
